import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import { useState, useCallback, useContext, useEffect } from "react";
import useAxiosPrivate from "../../../../../hooks/useAxiosPrivate";
import API_URLS from "../../../../../constants/apiConstants";
import { BiX, BiCloudUpload } from "react-icons/bi";
import AuthContext from "../../../../../context/AuthProvider";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Select from "react-select";

export default function AddDocumentModal({ open, onClose, onSuccess }) {
  const axiosPrivate = useAxiosPrivate();
  const { school } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [targetAudience, setTargetAudience] = useState(null);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [roomOptions, setRoomOptions] = useState([]);
  const [files, setFiles] = useState([]);
  const [isAllRooms, setIsAllRooms] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open && school?.id) {
      axiosPrivate.get(API_URLS.getSchoolRooms(school.id))
        .then(res => {
          const options = res.data.map(room => ({ value: room.id, label: room.ClassName }));
          setRoomOptions(options);
        }).catch(err => console.error(err));
    }
  }, [open, school?.id, axiosPrivate]);

  const resetForm = useCallback(() => {
    setTitle("");
    setDescription("");
    setTargetAudience(null);
    setSelectedRooms([]);
    setFiles([]);
    setIsAllRooms(false);
    setErrors({});
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = t("document_model.title_required");
    if (!targetAudience) newErrors.audience = t("document_model.audience_required");
    if (!isAllRooms && selectedRooms.length === 0) newErrors.rooms = t("document_model.rooms_required");
    if (files.length === 0) newErrors.files = t("document_model.files_required");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors(prev => ({ ...prev, server: undefined }));

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("nursery", school.id);
    formData.append("target_audience", targetAudience.value);

    if (isAllRooms) {
      roomOptions.forEach(room => formData.append("rooms", room.value));
    } else {
      selectedRooms.forEach(room => formData.append("rooms", room.value));
    }

    files.forEach(file => formData.append("files", file));

    try {
      await axiosPrivate.post(API_URLS.createDocumentGroup(), formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      onSuccess();
      handleClose();
    } catch (error) {
      console.error("Request error:", error);
      setErrors((prev) => ({
        ...prev,
        server: error.response?.data?.message || t("form.genericError")
      }));
      toast.error(isAr ? "حدث خطأ أثناء الرفع" : "Upload failed");
    } finally {
      setIsLoading(false);
    }
  };

 
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? 'rgb(211, 153, 228)' : base.borderColor,
      boxShadow: state.isFocused ? '0 0 0 1px rgb(205, 145, 223)' : base.boxShadow,
      '&:hover': {
        borderColor: 'rgb(194, 127, 214)'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? 'rgb(148, 9, 190)' 
        : state.isFocused 
        ? '#8a07b11a' 
        : base.backgroundColor,
      color: state.isSelected ? '#fff' : base.color,
      '&:active': {
        backgroundColor: '#730595ff'
      }
    }),
    input: (base) => ({
      ...base,
      "input:focus": {
        boxShadow: "none !important", 
      }
    })
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="modalStyle">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h5" fontWeight="bold" color="secondary">
            {t("document_model.add_document")}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <BiX />
          </IconButton>
        </Box>

        <Box mb={3}>
          <Typography variant="body1" component="label" display="block" color="secondary" gutterBottom>
            {t("document_model.title")} <span className="text-danger">*</span>
          </Typography>
          <input
            type="text"
            value={title}
            className={`form-control ${errors.title ? "is-invalid" : ""}`}
            onChange={(e) => {
              setTitle(e.target.value);
              setErrors((prev) => ({ ...prev, title: undefined }));
            }}
            placeholder={t("document_model.title_placeholder")}
            disabled={isLoading}
          />
          {errors.title && <Typography color="error" variant="caption">{errors.title}</Typography>}
        </Box>

        <Box mb={3}>
          <Typography variant="body1" component="label" display="block" color="secondary" gutterBottom>
            {t("document_model.description")}
          </Typography>
          <textarea
            className="form-control"
            rows="3"
            style={{ resize: 'none' }}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("document_model.description_placeholder")}
            disabled={isLoading}
          />
        </Box>

        <Box mb={3}>
          <Typography variant="body1" component="label" display="block" color="secondary" gutterBottom>
            {t("document_model.audience")} <span className="text-danger">*</span>
          </Typography>
          <Select
            options={[
              { value: 'staff', label: t("document_model.staff") },
              { value: 'parents', label: t("document_model.parents") }
            ]}
            value={targetAudience}
            onChange={(value) => {
              setTargetAudience(value);
              setErrors((prev) => ({ ...prev, audience: undefined }));
            }}
            placeholder={isAr ? "اختر..." : "Select..."}
            isDisabled={isLoading}
            isSearchable={false}
            styles={customSelectStyles}
          />
          {errors.audience && <Typography color="error" variant="caption">{errors.audience}</Typography>}
        </Box>

        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body1" component="label" color="secondary">
              {t("document_model.rooms")} {!isAllRooms && <span className="text-danger">*</span>}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAllRooms}
                  onChange={(e) => {
                    setIsAllRooms(e.target.checked);
                    setErrors((prev) => ({ ...prev, rooms: undefined }));
                  }}
                  disabled={isLoading}
                />
              }
              label={<Typography variant="body2">{t("document_model.all_rooms")}</Typography>}
            />
          </Box>
          {!isAllRooms && (
            <>
              <Select
                isMulti
                options={roomOptions}
                value={selectedRooms}
                onChange={(value) => {
                  setSelectedRooms(value);
                  setErrors((prev) => ({ ...prev, rooms: undefined }));
                }}
                placeholder={isAr ? "اختر الفصول..." : "Select rooms..."}
                isDisabled={isLoading}
                isSearchable={true}
                styles={customSelectStyles}
              />
              {errors.rooms && <Typography color="error" variant="caption">{errors.rooms}</Typography>}
            </>
          )}
        </Box>

        <Box mb={3}>
          <Typography variant="body1" component="label" display="block" color="secondary" gutterBottom>
            {t("document_model.upload_documents")} <span className="text-danger">*</span>
          </Typography>
          <label
            className={`d-flex flex-column align-items-center justify-content-center border rounded p-3 w-100 ${errors.files ? "border-danger" : ""}`}
            style={{
              borderStyle: 'dashed',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              backgroundColor: '#fcfafaff',
              borderColor: errors.files ? '#dc3545' : '#dee2e6'
            }}
          >
            <input
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const newFiles = Array.from(e.target.files);
                if (newFiles.length > 0) {
                  setFiles(prev => [...prev, ...newFiles]);
                  setErrors((prev) => ({ ...prev, files: undefined }));
                }
                e.target.value = null; 
              }}
              disabled={isLoading}
            />
            <BiCloudUpload size={35} color="#730595ff" />
            <Typography variant="body2" mt={1} color="textSecondary">
              {t("document_model.click_to_upload")}
            </Typography>
          </label>
          
          {files.length > 0 && (
            <Box mt={2} sx={{ maxHeight: '150px', overflowY: 'auto' }}>
              {files.map((f, i) => (
                <div key={i} className="d-flex justify-content-between align-items-center small bg-light p-2 mb-1 rounded border">
                  <span className="text-truncate" style={{ maxWidth: '85%' }}>{f.name}</span>
                  <IconButton
                    size="small"
                    onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
                    disabled={isLoading}
                  >
                    <BiX size={18} color="red" />
                  </IconButton>
                </div>
              ))}
            </Box>
          )}
          {errors.files && <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>{errors.files}</Typography>}
        </Box>

        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            className="submitButton"
          >
            {isAr ? "حفظ" : "Save"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}